import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Building2, Inbox, LogOut, Plus, Pencil, Trash2, Check, X, Upload, Star, GripVertical,
} from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, rectSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import ContentManager from "../components/admin/ContentManager";
import TextsManager from "../components/admin/TextsManager";

const KIND_LABELS = {
  estimation: "Estimation",
  alerte: "Alerte personnalisée",
  achat: "Projet d'achat",
  contact: "Contact bien",
  neuf: "Programme neuf",
};

const CITY_FIELDS = [
  { key: "name", label: "Nom de la ville" },
  { key: "description", label: "Description", textarea: true },
  { key: "image", label: "Photo", image: true },
];

const REVIEW_FIELDS = [
  { key: "name", label: "Nom du client" },
  { key: "context", label: "Contexte (ex. Achat à Nice · Avis Google)" },
  { key: "quote", label: "Avis", textarea: true, rows: 4 },
];

const ARTICLE_FIELDS = [
  { key: "title", label: "Titre de l'article" },
  { key: "category", label: "Catégorie (ex. Vendre, Acheter, Investissement)" },
  { key: "readTime", label: "Temps de lecture (ex. 5 min)" },
  { key: "image", label: "Image", image: true },
  { key: "excerpt", label: "Résumé (affiché sur la carte)", textarea: true },
  { key: "body", label: "Article complet (paragraphes séparés par une ligne vide)", textarea: true, rows: 7 },
];

const SortablePhoto = ({ url, index, onSetMain, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: url });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      data-testid={`photo-item-${index}`}
      className={`group relative aspect-[4/3] overflow-hidden rounded-lg border ${
        isDragging ? "z-10 border-gold shadow-2xl" : "border-white/10"
      }`}
    >
      <img src={url} alt="" draggable={false} className="pointer-events-none h-full w-full object-cover" />
      {index === 0 && (
        <span className="absolute left-1.5 top-1.5 rounded-full bg-gold px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-[#0B132B]">
          Principale
        </span>
      )}
      <button
        type="button"
        {...attributes}
        {...listeners}
        data-testid={`photo-drag-${index}`}
        title="Glisser pour réorganiser"
        className="absolute bottom-1.5 left-1.5 cursor-grab rounded-full bg-black/60 p-1.5 text-white/80 backdrop-blur transition-colors hover:bg-gold hover:text-[#0B132B] active:cursor-grabbing"
      >
        <GripVertical size={12} />
      </button>
      <div className="absolute right-1.5 top-1.5 flex gap-1 opacity-90 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
        {index !== 0 && (
          <button
            type="button"
            onClick={() => onSetMain(index)}
            data-testid={`photo-set-main-${index}`}
            title="Définir comme photo principale"
            className="rounded-full bg-black/60 p-1.5 text-gold backdrop-blur transition-colors hover:bg-gold hover:text-[#0B132B]"
          >
            <Star size={12} />
          </button>
        )}
        <button
          type="button"
          onClick={() => onDelete(index)}
          data-testid={`photo-delete-${index}`}
          title="Retirer cette photo"
          className="rounded-full bg-black/60 p-1.5 text-red-300 backdrop-blur transition-colors hover:bg-red-500 hover:text-white"
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
};

const formatPrice = (n) =>
  `${String(n || 0).replace(/\B(?=(\d{3})+(?!\d))/g, " ")} €`;

const EMPTY_FORM = {
  title: "", type: "Villa", ville: "", ville_libre: "", quartier: "", exclusive: false,
  terrasse: false, jardin: false,
  price_raw: "", bedrooms: "", surface: "", tags: "", description: "", photos: [],
};

const fieldCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-light text-white placeholder:text-white/40 outline-none transition-colors focus:border-gold";

export default function Admin() {
  const { user, loading, login, logout } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("biens");
  const [properties, setProperties] = useState([]);
  const [leads, setLeads] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [cityOptions, setCityOptions] = useState([]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const onPhotosDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setForm((f) => {
      const oldIndex = f.photos.indexOf(active.id);
      const newIndex = f.photos.indexOf(over.id);
      if (oldIndex === -1 || newIndex === -1) return f;
      return { ...f, photos: arrayMove(f.photos, oldIndex, newIndex) };
    });
  };

  const refresh = () => {
    api.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/cities").then((r) => setCityOptions(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (user) refresh();
  }, [user]);

  const doLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await login(email, password);
      toast.success("Bienvenue dans votre espace.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Identifiants incorrects.");
    }
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setPhotoUrl("");
    setEditing({ id: null });
  };

  const openEdit = (p) => {
    const tags = p.tags || [];
    const [villePart, ...restPart] = (p.location || "").split(" — ");
    const known = cityOptions.some((c) => c.name === villePart);
    setForm({
      title: p.title,
      type: p.type,
      ville: villePart ? (known ? villePart : "__autre") : "",
      ville_libre: villePart && !known ? villePart : "",
      quartier: restPart.join(" — "),
      exclusive: tags.includes("Exclusivité"),
      terrasse: tags.includes("Terrasse"),
      jardin: tags.includes("Jardin"),
      price_raw: String(p.price_raw),
      bedrooms: String(p.bedrooms),
      surface: p.surface,
      tags: tags.filter((t) => !["Exclusivité", "Terrasse", "Jardin"].includes(t)).join(", "),
      description: p.description || "",
      photos: [p.image, ...(p.gallery || [])].filter(Boolean),
    });
    setPhotoUrl("");
    setEditing(p);
  };

  const saveProperty = async (e) => {
    e.preventDefault();
    if (!form.photos.length) {
      toast.error("Ajoutez au moins une photo au bien.");
      return;
    }
    setSaving(true);
    const priceNum = parseInt(String(form.price_raw).replace(/\D/g, ""), 10) || 0;
    const villeFinale = form.ville === "__autre" ? form.ville_libre.trim() : form.ville;
    const location = villeFinale + (form.quartier.trim() ? ` — ${form.quartier.trim()}` : "");
    const tags = form.tags.split(",").map((t) => t.trim()).filter((t) => t && !["Exclusivité", "Terrasse", "Jardin"].includes(t));
    if (form.jardin) tags.unshift("Jardin");
    if (form.terrasse) tags.unshift("Terrasse");
    if (form.exclusive) tags.unshift("Exclusivité");
    const payload = {
      title: form.title,
      type: form.type,
      location,
      price: formatPrice(priceNum),
      price_raw: priceNum,
      bedrooms: parseInt(form.bedrooms, 10) || 0,
      surface: form.surface,
      tags,
      description: form.description,
      image: form.photos[0],
      gallery: form.photos.slice(1),
    };
    try {
      if (editing?.id) {
        await api.put(`/properties/${editing.id}`, payload);
        toast.success("Bien mis à jour.");
      } else {
        await api.post("/properties", payload);
        toast.success("Bien ajouté au portfolio.");
      }
      setEditing(null);
      refresh();
    } catch {
      toast.error("Enregistrement impossible, vérifiez les champs.");
    } finally {
      setSaving(false);
    }
  };

  const removeProperty = async (id) => {
    if (!window.confirm("Supprimer définitivement ce bien ?")) return;
    await api.delete(`/properties/${id}`).catch(() => {});
    toast.success("Bien supprimé.");
    refresh();
  };

  const markRead = async (id) => {
    await api.patch(`/leads/${id}/read`).catch(() => {});
    refresh();
  };

  const addPhotoUrl = () => {
    const u = photoUrl.trim();
    if (!u) return;
    setForm((f) => ({ ...f, photos: [...f.photos, u] }));
    setPhotoUrl("");
  };

  const removePhoto = (i) =>
    setForm((f) => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));

  const setMainPhoto = (i) =>
    setForm((f) => {
      const photos = [...f.photos];
      const [picked] = photos.splice(i, 1);
      photos.unshift(picked);
      return { ...f, photos };
    });

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, photos: [...f.photos, ...data.urls] }));
      toast.success(`${data.urls.length} photo${data.urls.length > 1 ? "s" : ""} téléversée${data.urls.length > 1 ? "s" : ""}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Échec du téléversement, réessayez.");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B132B]">
        <span className="font-serif text-2xl tracking-[0.3em] text-gold">KEMELIA</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div data-testid="admin-login-page" className="flex min-h-screen items-center justify-center bg-[#0B132B] px-4">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#1C2541] p-10 shadow-2xl">
          <div className="flex flex-col items-center">
            <img src="/kemelia-logo.png" alt="Kemelia Estate" className="w-24 rounded-2xl" />
            <h1 className="mt-6 font-serif text-2xl font-semibold tracking-[0.25em] text-white">ESPACE AGENCE</h1>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Accès réservé</p>
          </div>
          <form onSubmit={doLogin} className="mt-8 space-y-4">
            <input
              type="email" required placeholder="Email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email-input" className={fieldCls}
            />
            <input
              type="password" required placeholder="Mot de passe" value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input" className={fieldCls}
            />
            {error && (
              <p data-testid="admin-login-error" className="rounded-lg bg-red-500/10 px-4 py-2.5 text-sm text-red-300">
                {error}
              </p>
            )}
            <button
              type="submit" data-testid="admin-login-button"
              className="w-full rounded-xl bg-gold px-6 py-3.5 text-sm font-semibold text-[#0B132B] transition-colors hover:bg-sand"
            >
              Se connecter
            </button>
          </form>
          <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">
            Démo : admin@kemelia-estate.fr · Riviera2026!
          </p>
        </div>
      </div>
    );
  }

  const unread = leads.filter((l) => !l.read).length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0B132B] text-white">
      <header className="border-b border-white/10 bg-[#1C2541]/60 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            <span className="font-serif text-xl font-semibold tracking-[0.25em]">KEMELIA</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Back-office</span>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-medium text-white/70 transition-colors hover:border-gold hover:text-gold"
          >
            <LogOut size={14} /> Déconnexion
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-8">
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-[#1C2541] p-6" data-testid="admin-stat-biens">
            <div className="flex items-center gap-3 text-white/50"><Building2 size={16} /><span className="font-mono text-[10px] uppercase tracking-[0.2em]">Biens en ligne</span></div>
            <div className="mt-3 font-serif text-4xl font-semibold text-gold">{properties.length}</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#1C2541] p-6" data-testid="admin-stat-unread">
            <div className="flex items-center gap-3 text-white/50"><Inbox size={16} /><span className="font-mono text-[10px] uppercase tracking-[0.2em]">Demandes à traiter</span></div>
            <div className="mt-3 font-serif text-4xl font-semibold text-gold">{unread}</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#1C2541] p-6" data-testid="admin-stat-total">
            <div className="flex items-center gap-3 text-white/50"><Inbox size={16} /><span className="font-mono text-[10px] uppercase tracking-[0.2em]">Demandes totales</span></div>
            <div className="mt-3 font-serif text-4xl font-semibold text-gold">{leads.length}</div>
          </div>
        </div>

        <div className="mt-10 flex items-center justify-between">
          <div className="flex gap-2">
            {[
              { key: "biens", label: "Biens" },
              { key: "demandes", label: `Demandes${unread ? ` (${unread})` : ""}` },
              { key: "contenu", label: "Contenu du site" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                  tab === t.key ? "bg-gold text-[#0B132B]" : "border border-white/15 text-white/70 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {tab === "biens" && (
            <button
              onClick={openCreate}
              data-testid="admin-add-property"
              className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-[#0B132B] transition-colors hover:bg-sand"
            >
              <Plus size={15} /> Ajouter un bien
            </button>
          )}
        </div>

        {tab === "biens" && (
          <div className="mt-6 overflow-hidden rounded-2xl border border-white/10" data-testid="admin-properties-list">
            {properties.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-property-row-${p.id}`}
                className="flex flex-wrap items-center gap-4 border-b border-white/5 bg-[#1C2541] px-5 py-4 last:border-b-0"
              >
                <img src={p.image} alt="" className="h-14 w-20 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-serif text-lg text-white">{p.title}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                    {p.type} · {p.location} · {p.bedrooms} ch · {p.surface}
                  </p>
                </div>
                <span className="font-serif text-lg text-gold">{p.price}</span>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(p)} data-testid={`admin-edit-${p.id}`} aria-label="Modifier" className="rounded-full border border-white/15 p-2.5 text-white/60 transition-colors hover:border-gold hover:text-gold">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => removeProperty(p.id)} data-testid={`admin-delete-${p.id}`} aria-label="Supprimer" className="rounded-full border border-white/15 p-2.5 text-white/60 transition-colors hover:border-red-400 hover:text-red-400">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
            {properties.length === 0 && (
              <p className="bg-[#1C2541] px-5 py-12 text-center font-serif text-lg italic text-white/40">
                Aucun bien pour le moment.
              </p>
            )}
          </div>
        )}

        {tab === "demandes" && (
          <div className="mt-6 space-y-4" data-testid="admin-leads-list">
            {leads.map((l) => (
              <div
                key={l.id}
                data-testid={`admin-lead-${l.id}`}
                className={`rounded-2xl border bg-[#1C2541] p-6 transition-colors ${
                  l.read ? "border-white/5 opacity-60" : "border-gold/30"
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${
                      l.kind === "estimation" ? "bg-gold/15 text-gold" : l.kind === "alerte" ? "bg-terracotta/20 text-terracotta" : "bg-white/10 text-white/70"
                    }`}>
                      {KIND_LABELS[l.kind] || l.kind}
                    </span>
                    {!l.read && <span className="h-2 w-2 rounded-full bg-gold" />}
                  </div>
                  <span className="font-mono text-[10px] text-white/30">
                    {new Date(l.created_at).toLocaleString("fr-FR")}
                  </span>
                </div>
                <p className="mt-3 font-serif text-lg text-white">{l.name}</p>
                <p className="text-sm font-light text-white/60">
                  {l.email}{l.phone ? ` · ${l.phone}` : ""}
                </p>
                {l.message && <p className="mt-2 text-sm font-light leading-relaxed text-white/70">{l.message}</p>}
                {l.details && Object.keys(l.details).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {Object.entries(l.details).map(([k, v]) => (
                      <span key={k} className="rounded-full bg-white/5 px-3 py-1 font-mono text-[10px] text-white/50">
                        {k} : {String(v)}
                      </span>
                    ))}
                  </div>
                )}
                {!l.read && (
                  <button
                    onClick={() => markRead(l.id)}
                    data-testid={`admin-lead-read-${l.id}`}
                    className="mt-4 inline-flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2 text-xs font-medium text-gold transition-colors hover:bg-gold hover:text-[#0B132B]"
                  >
                    <Check size={13} /> Marquer comme traité
                  </button>
                )}
              </div>
            ))}
            {leads.length === 0 && (
              <p className="rounded-2xl border border-white/10 bg-[#1C2541] px-5 py-12 text-center font-serif text-lg italic text-white/40">
                Aucune demande pour le moment.
              </p>
            )}
          </div>
        )}
        {tab === "contenu" && (
          <div className="mt-6 space-y-6" data-testid="admin-content-tab">
            <TextsManager />
            <ContentManager
              title="Villes & secteurs (section Côte d'Azur de l'accueil)"
              endpoint="cities"
              fields={CITY_FIELDS}
              itemTitle={(i) => `${i.name}${i.count ? ` — ${i.count}` : ""}`}
              itemImage={(i) => i.image}
            />
            <ContentManager
              title="Avis clients (accueil)"
              endpoint="reviews"
              fields={REVIEW_FIELDS}
              itemTitle={(i) => `${i.name}${i.context ? ` — ${i.context}` : ""}`}
            />
            <ContentManager
              title="Articles de la rubrique Conseils"
              endpoint="articles"
              fields={ARTICLE_FIELDS}
              itemTitle={(i) => i.title}
              itemImage={(i) => i.image}
            />
          </div>
        )}
      </main>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl border-white/10 bg-[#1C2541] text-white" data-testid="property-form-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-white">
              {editing?.id ? "Modifier le bien" : "Ajouter un bien"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveProperty} className="mt-4 grid max-h-[70vh] gap-3 overflow-y-auto pr-1 sm:grid-cols-2">
            <input required placeholder="Titre du bien" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="property-form-title" className={fieldCls + " sm:col-span-2"} />
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="property-form-type" className={fieldCls}>
              <option value="Villa">Villa</option>
              <option value="Appartement">Appartement</option>
              <option value="Maison">Maison</option>
              <option value="Local commercial">Local commercial</option>
            </select>
            <select required value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} data-testid="property-form-city" className={fieldCls}>
              <option value="" disabled>Ville</option>
              {cityOptions.map((c) => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
              <option value="__autre">Autre ville…</option>
            </select>
            {form.ville === "__autre" && (
              <input required placeholder="Nom de la ville" value={form.ville_libre} onChange={(e) => setForm({ ...form, ville_libre: e.target.value })} data-testid="property-form-city-custom" className={fieldCls} />
            )}
            <input placeholder="Quartier / précision (ex. Jean Médecin)" value={form.quartier} onChange={(e) => setForm({ ...form, quartier: e.target.value })} data-testid="property-form-district" className={fieldCls} />
            <div>
              <div className="relative">
                <input
                  required
                  inputMode="numeric"
                  placeholder="Prix (ex. 630000)"
                  value={form.price_raw}
                  onChange={(e) => setForm({ ...form, price_raw: e.target.value.replace(/[^\d\s]/g, "") })}
                  data-testid="property-form-price-raw"
                  className={fieldCls + " pr-9"}
                />
                <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 font-serif text-lg text-gold">€</span>
              </div>
              {parseInt(String(form.price_raw).replace(/\D/g, ""), 10) > 0 && (
                <p data-testid="price-preview" className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-gold/80">
                  Affiché sur le site : {formatPrice(parseInt(String(form.price_raw).replace(/\D/g, ""), 10))}
                </p>
              )}
            </div>
            <input required type="number" placeholder="Chambres" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} data-testid="property-form-bedrooms" className={fieldCls} />
            <input required placeholder="Surface (ex. 250 m²)" value={form.surface} onChange={(e) => setForm({ ...form, surface: e.target.value })} data-testid="property-form-surface" className={fieldCls} />
            <input placeholder="Autres étiquettes, séparées par des virgules (Vue mer, Terrasse, Piscine…)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="property-form-tags" className={fieldCls + " sm:col-span-2"} />
            <div className="flex flex-col gap-3 sm:col-span-2 sm:flex-row">
              <label
                data-testid="property-form-exclusive-label"
                className={`flex flex-1 cursor-pointer items-center gap-3 rounded-xl border px-4 py-3.5 transition-colors ${
                  form.exclusive ? "border-gold bg-gold/10" : "border-white/10 hover:border-gold/40"
                }`}
              >
                <input
                  type="checkbox"
                  checked={form.exclusive}
                  onChange={(e) => setForm({ ...form, exclusive: e.target.checked })}
                  data-testid="property-form-exclusive"
                  className="h-4 w-4 accent-[#D4AF37]"
                />
                <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Exclusivité</span>
              </label>
              <label
                data-testid="property-form-terrasse-label"
                className={`flex flex-1 cursor-pointer items-center gap-3 rounded-xl border px-4 py-3.5 transition-colors ${
                  form.terrasse ? "border-gold bg-gold/10" : "border-white/10 hover:border-gold/40"
                }`}
              >
                <input
                  type="checkbox"
                  checked={form.terrasse}
                  onChange={(e) => setForm({ ...form, terrasse: e.target.checked })}
                  data-testid="property-form-terrasse"
                  className="h-4 w-4 accent-[#D4AF37]"
                />
                <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Terrasse</span>
              </label>
              <label
                data-testid="property-form-jardin-label"
                className={`flex flex-1 cursor-pointer items-center gap-3 rounded-xl border px-4 py-3.5 transition-colors ${
                  form.jardin ? "border-gold bg-gold/10" : "border-white/10 hover:border-gold/40"
                }`}
              >
                <input
                  type="checkbox"
                  checked={form.jardin}
                  onChange={(e) => setForm({ ...form, jardin: e.target.checked })}
                  data-testid="property-form-jardin"
                  className="h-4 w-4 accent-[#D4AF37]"
                />
                <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Jardin</span>
              </label>
            </div>
            <div className="space-y-3 sm:col-span-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                Photos du bien ({form.photos.length})
              </p>
              {form.photos.length > 0 && (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onPhotosDragEnd}>
                  <SortableContext items={form.photos} strategy={rectSortingStrategy}>
                    <div data-testid="photo-manager" className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                      {form.photos.map((url, i) => (
                        <SortablePhoto
                          key={url}
                          url={url}
                          index={i}
                          onSetMain={setMainPhoto}
                          onDelete={removePhoto}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
              <div className="flex gap-2">
                <input
                  placeholder="Coller l'URL d'une photo…"
                  value={photoUrl}
                  onChange={(e) => setPhotoUrl(e.target.value)}
                  data-testid="photo-url-input"
                  className={fieldCls}
                />
                <button
                  type="button"
                  onClick={addPhotoUrl}
                  data-testid="photo-add-url"
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-white/15 px-4 text-sm text-white/70 transition-colors hover:border-gold hover:text-gold"
                >
                  <Plus size={14} /> Ajouter
                </button>
              </div>
              <label
                data-testid="property-form-upload-label"
                className={`flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-white/25 px-4 py-4 text-sm text-white/70 transition-colors hover:border-gold hover:text-gold ${uploading ? "pointer-events-none opacity-50" : ""}`}
              >
                <Upload size={16} />
                {uploading ? "Téléversement en cours…" : "Téléverser des photos depuis mon appareil"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  className="hidden"
                  onChange={uploadPhotos}
                  data-testid="property-form-upload"
                />
              </label>
              <p className="text-xs font-light text-white/40">
                La première photo est la photo principale. Touchez une photo pour la retirer ou la définir comme principale.
              </p>
            </div>
            <textarea rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="property-form-description" className={fieldCls + " resize-none sm:col-span-2"} />
            <div className="flex gap-3 sm:col-span-2">
              <button type="submit" disabled={saving} data-testid="property-form-submit" className="flex-1 rounded-xl bg-gold px-6 py-3.5 text-sm font-semibold text-[#0B132B] transition-colors hover:bg-sand disabled:opacity-60">
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
              <button type="button" onClick={() => setEditing(null)} data-testid="property-form-cancel" className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-5 text-sm text-white/70 transition-colors hover:text-white">
                <X size={14} /> Annuler
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
